import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SingleInsuranceInfoProps } from "../../../../../pages/insuranceDetails/components/singleInsuranceInfo";
import { generateInsuranceDetails } from "../../../../../pages/insuranceDetails/utils/generateInsuranceDetails";
import generatePbmEligibilityData from "../../../../../pages/insuranceDetails/utils/generatePbmEligibilityData";
import {
  fetchAllInsuranceDetailsForOrder,
  resetInsuranceDetails,
} from "../../../../../redux/slice/cmm/insuranceDetailsSlice";
import { fetchPatientEligibilityDetails } from "../../../../../redux/slice/cmm/patientEligibilitySlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";

interface UseInsuranceDetailsProps {
  rowId?: string | number;
}

interface UseInsuranceDetailsReturn {
  finalInsuranceDetails: SingleInsuranceInfoProps[];
  loading: boolean;
  hasData: boolean;
}

export const useInsuranceDetails = ({
  rowId,
}: UseInsuranceDetailsProps): UseInsuranceDetailsReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: insuranceData, loading: insuranceLoading } = useSelector(
    (state: RootState) => state.insuranceDetails,
  );
  const { data: patientEligibilityData, loading: patientEligibilityLoading } =
    useSelector((state: RootState) => state.patientEligibility);

  // Effect to fetch insurance and eligibility data when rowId changes
  useEffect(() => {
    if (rowId) {
      dispatch(fetchAllInsuranceDetailsForOrder(String(rowId)));
      dispatch(fetchPatientEligibilityDetails(String(rowId)));
    }

    return () => {
      dispatch(resetInsuranceDetails());
    };
  }, [rowId, dispatch]);

  // Memoize insurance details generation
  const insuranceDetails = useMemo(
    () => (insuranceData ? generateInsuranceDetails(insuranceData) : []),
    [insuranceData],
  );

  // Memoize PBM eligibility data generation
  const pbmEligibilityData = useMemo(
    () =>
      patientEligibilityData && patientEligibilityData?.length > 0
        ? patientEligibilityData.map((detail) =>
            generatePbmEligibilityData(detail),
          )
        : [],
    [patientEligibilityData],
  );

  // Combine insurance details and PBM eligibility data
  const finalInsuranceDetails: SingleInsuranceInfoProps[] = useMemo(() => {
    return [...insuranceDetails, ...pbmEligibilityData];
  }, [insuranceDetails, pbmEligibilityData]);

  const loading = insuranceLoading || patientEligibilityLoading;
  const hasData = Boolean(insuranceData && insuranceData.length > 0);

  return {
    finalInsuranceDetails,
    loading,
    hasData,
  };
};
